// ═══════════════════════════════════════════════════
//  Oversized factor, resolved in exactly one place
//
//  The Cria Library (client computeTemplateCost) and the Cria price API
//  (server banquetteResult) both multiply a banquette's ex-fabric total by
//  this factor. Each side calls parseOversizeRules once per load and
//  resolveOversize once per component, and nothing else, so the two cannot
//  drift on the math, the order of the steps, or what unreadable rules do —
//  the arrangement button-hardware.ts set up for the eight button blocks.
//
//  Call sites decide only where the charge and the warnings go, never what
//  either one is. See docs/superpowers/specs/2026-09-23-oversized-cost-design.md.
// ═══════════════════════════════════════════════════

import {
  OVERSIZE_AXES,
  OVERSIZE_PRODUCT_TYPES,
  OversizeRulesSchema,
} from '../schemas/oversize-settings.schema';
import type {
  OversizeProductType,
  OversizeRuleRow,
  OversizeRules,
} from '../schemas/oversize-settings.schema';

export type OversizeRulesState =
  | { status: 'none' }                                  // no settings row
  | { status: 'ok'; rules: OversizeRules }
  | { status: 'unreadable' };

/** The note both sides return beside a null `exFabric` when the rules are unreadable. */
export const OVERSIZE_UNREADABLE_NOTE = 'oversize rules unreadable';

/**
 * The profile defaults the three cost helpers apply when a config omits one
 * (banquette-cost, seat-cushion-cost and back-cushion-cost, server and client).
 * Configs reach the server as raw JSON.parse with no schema defaults, so the
 * factor must measure with the same fallbacks or it measures a size that was
 * never priced. The helpers do not read this constant; a change to one of
 * their defaults must be made here too.
 */
export const OVERSIZE_DIM_DEFAULTS: Record<OversizeProductType, { height: number; depth: number }> = {
  banquette_base: { height: 20, depth: 18 },
  banquette_seat_cushion: { height: 4, depth: 20 },
  banquette_back_cushion: { height: 18, depth: 6 },
};

export function isOversizeProductType(t: string): t is OversizeProductType {
  return (OVERSIZE_PRODUCT_TYPES as readonly string[]).includes(t);
}

/**
 * Read the stored `rules` value — the column, not the whole settings row,
 * whose `id` and timestamps fail the schema's `.strict()`. The server passes
 * `row?.rules`, the client `data?.rules`.
 *
 * null/undefined is `none` (no row). A string is JSON.parsed first, for a
 * driver or raw query that hands jsonb back as text. A JSON.parse or schema
 * failure is `unreadable`, never `none`: reading a damaged rule set as "no
 * rules" would silently drop every oversized charge in one Cria sync.
 */
export function parseOversizeRules(raw: unknown): OversizeRulesState {
  if (raw === null || raw === undefined) return { status: 'none' };

  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return { status: 'unreadable' };
    }
  }

  const parsed = OversizeRulesSchema.safeParse(value);
  return parsed.success ? { status: 'ok', rules: parsed.data } : { status: 'unreadable' };
}

export interface OversizeDims { width: number; depth: number; height: number }

function dimsFor(productType: OversizeProductType, config: any): OversizeDims {
  const defaults = OVERSIZE_DIM_DEFAULTS[productType];
  const lfQty = config?.lf_quantity ?? 0;
  // The helpers' own section count expression, so 0 or absent divides by 1.
  const sectionCount = Math.max(1, Math.floor(config?.section_count ?? 1));
  return {
    width: (lfQty * 12) / sectionCount,
    depth: config?.profile_depth ?? defaults.depth,
    height: config?.profile_height ?? defaults.height,
  };
}

/**
 * The dimensions the factor measures, from the effective config (after any
 * Cria size override and section recount). Width is one section's width — the
 * size of one physical piece — not the whole run. Null for an out-of-scope type.
 */
export function oversizeDimsOf(productType: string, config: any): OversizeDims | null {
  return isOversizeProductType(productType) ? dimsFor(productType, config) : null;
}

export interface OversizeResult {
  /** >= 1. Multiplies `exFabric`; the charge is `exFabric * (factor - 1)`. */
  factor: number;
  extras: { width: number; depth: number; height: number };
  warnings: string[];
  /** Rules were unreadable. Callers return `exFabric: null` with OVERSIZE_UNREADABLE_NOTE. */
  unreadable: boolean;
}

function flat(unreadable = false): OversizeResult {
  return { factor: 1, extras: { width: 0, depth: 0, height: 0 }, warnings: [], unreadable };
}

/** Step rows: the row with the greatest `from` at or below `value`, else no extra. */
function extraFor(rows: readonly OversizeRuleRow[], value: number): number {
  let match: OversizeRuleRow | undefined;
  for (const row of rows) {
    if (row.from <= value && (!match || row.from > match.from)) match = row;
  }
  return match ? match.factor - 1 : 0;
}

/**
 * The oversized factor for one component, stopping at the first that applies:
 *
 *   1. not a banquette base, seat cushion or back cushion -> 1
 *   2. `config.oversize_enabled === false` -> 1. Before the unreadable check:
 *      an opted-out template's price cannot depend on the rules.
 *   3. no settings row -> 1
 *   4. unreadable rules -> 1 with `unreadable: true`
 *   5. each axis WITH rows: a non-finite value warns and adds nothing; a
 *      finite one adds its matching row's `factor - 1`. An axis with no rows
 *      is skipped before the finiteness check, so it never warns.
 *
 * Extras add rather than compound: height x1.5 and depth x2 give x2.5.
 */
export function resolveOversize(
  state: OversizeRulesState,
  productType: string,
  config: any,
): OversizeResult {
  if (!isOversizeProductType(productType)) return flat();
  if (config?.oversize_enabled === false) return flat();
  if (state.status === 'none') return flat();
  if (state.status === 'unreadable') return flat(true);

  const typeRules = state.rules[productType];
  const dims = dimsFor(productType, config);
  const extras = { width: 0, depth: 0, height: 0 };
  const warnings: string[] = [];

  for (const axis of OVERSIZE_AXES) {
    const rows = typeRules?.[axis] ?? [];
    if (rows.length === 0) continue;

    const value = dims[axis];
    if (!Number.isFinite(value)) {
      warnings.push(`oversize: ${axis} is not a finite number; no oversized charge applied for it`);
      continue;
    }
    extras[axis] = extraFor(rows, value);
  }

  return {
    factor: 1 + extras.width + extras.depth + extras.height,
    extras,
    warnings,
    unreadable: false,
  };
}

/** For the `Oversized x2.5` label: at most two decimals, trailing zeros dropped. */
export function formatOversizeFactor(factor: number): string {
  return String(Number(factor.toFixed(2)));
}
